import { CostChannel } from '@sg/graphql-schema';

import {
  useGetSharedLineItemProductForRestaurantByLineageKeyQuery,
  useGetSharedLineItemProductForRestaurantQuery,
} from '../../GraphQL/ShareLineItemScreen.generated';
import { useShareLineItemLocationDetails } from '../useShareLineItemLocationDetails';

// ─────────────────────────────────────────────────────────────────────────────

export const useShareLineItemRemoteData = (params: Params) => {
  const {
    baseProductLineageKey,
    baseProductSlug,
    selectedDeliveryAddressId,
    selectedRestaurantId,
  } = params;

  const costChannel = selectedDeliveryAddressId
    ? CostChannel.DeliveryCostChannel
    : CostChannel.DefaultCostChannel;

  // ─── Remote Data ─────────────────────────────────────────────────────

  const locationDetails = useShareLineItemLocationDetails({
    restaurantId: selectedRestaurantId,
    deliveryAddressId: selectedDeliveryAddressId,
  });

  const [lineageProductResponse] = useGetSharedLineItemProductForRestaurantByLineageKeyQuery({
    pause: !baseProductLineageKey || !selectedRestaurantId,
    requestPolicy: 'network-only',
    variables: {
      lineageKey: baseProductLineageKey ?? '',
      restaurantId: selectedRestaurantId ?? '',
      costChannel,
    },
  });

  // Items predating lineage keys still resolve by slug.
  const [productResponse] = useGetSharedLineItemProductForRestaurantQuery({
    pause: Boolean(baseProductLineageKey) || !baseProductSlug || !selectedRestaurantId,
    requestPolicy: 'network-only',
    variables: {
      baseProductSlug: baseProductSlug ?? '',
      restaurantId: selectedRestaurantId ?? '',
      costChannel,
    },
  });

  // ─── Derived ─────────────────────────────────────────────────────────

  const isFetchingProduct = lineageProductResponse.fetching || productResponse.fetching;
  const isFetchingSharedLinkProduct = isFetchingProduct || locationDetails.isFetching;

  const productForRestaurant =
    lineageProductResponse.data?.productForRestaurantByLineageKey ??
    productResponse.data?.productForRestaurant ??
    undefined;
  const hasNoProductData = !isFetchingProduct && !productForRestaurant;
  const isUnavailableProduct = hasNoProductData || productForRestaurant?.outOfStock === true;

  // ─────────────────────────────────────────────────────────────────────

  return {
    isFetchingSharedLinkProduct,
    isUnavailableProduct,
    locationDetails,
    productForRestaurant,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  baseProductLineageKey?: string;
  baseProductSlug?: string;
  selectedDeliveryAddressId?: string;
  selectedRestaurantId?: string;
};
